import { FormGroup, FormLabel, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createNegotiation, CreateNegotiationPaylod, getNegotiationById, updateNegotiation } from "src/apis/negotiation";
import CompanySelect from "src/pages/components/CompanySelect";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import { Id } from "src/utils/types";

const initialValues: CreateNegotiationPaylod = {
  empresa: 0,
  negociacao: '',
}

const RegisterNegotiation: FC = (): JSX.Element => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<CreateNegotiationPaylod>(initialValues);
  const { id } = useParams();

  const isUpdate = useMemo<boolean>(() => Boolean(id), [id]);

  const resetFormState = (): void => {
    setError('');
    setSuccess(false);
    setFormValues(initialValues);
  }

  const fetchNegotiation = async (negotiationId: Id): Promise<void> => {
    try {
      const res = await getNegotiationById(negotiationId);

      setFormValues({
        empresa: res.empresa_id,
        negociacao: res.negociacao,
      })
      setError('');
    } catch (err: any) {
      setError(err?.message)
      setSuccess(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isUpdate) {
        await updateNegotiation(id, formValues)
      } else {
        await createNegotiation(formValues);
      }

      setError('');
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message);
      setSuccess(false);
    } finally {
      setLoading(true);
    }
  }

  const isSubmitDisabled = useMemo(() => {
    if (Boolean(error) || success) {
      return true;
    }

    return !Object.values(formValues).every((value) => Boolean(value))
  }, [error, success, formValues]);

  useEffect(() => {
    if (!id) return;

    fetchNegotiation(id);
  }, [id])

  return (
    <PageContainer title={isUpdate ? "Editando negociação" : "Adicionar negociação"} error={error} guard={isUserAdministrator}>
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <CompanySelect
          companyId={formValues.empresa}
          setCompanyId={(companyId: Id) => {
            setFormValues(previousValues => ({
              ...previousValues,
              empresa: companyId as number,
            }));
            setSuccess(false);
          }}
          setError={setError}
        />
        <FormGroup>
          <FormLabel>Negociação</FormLabel>
          <TextField
            multiline
            name="negociacao"
            type="text"
            value={formValues.negociacao}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                negociacao: e.target.value as string,
              }));
            }}
          />
        </FormGroup>

        <FormSaveAndResetButtons
          success={success}
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          resetFormState={resetFormState}
          isResetFormStateDisabled={isUpdate}
        />
      </form>
    </PageContainer>
  )
}

export default RegisterNegotiation;
